// 캘린더 일정 유형 — 단일 출처.
//
// 여기 모으기 전에는 유형 목록과 색상표가 6개 파일에 복붙돼 있었다
// (CalendarSidebar / CalendarPcLayout / CalendarMobileLayout의 색상맵 3벌,
//  EventCreateModal / EventDetailModal의 유형 배열). 유형을 하나 추가하려면
// 전부 찾아 고쳐야 했고, 한 곳만 빠뜨리면 "필터엔 있는데 색이 없는" 식으로
// 조용히 어긋난다. 새 유형은 이 파일만 고치면 된다.
//
// ⚠️ event_type 문자열은 백엔드 CalendarEvent.EVENT_TYPES와 정확히 같아야 한다
// (choices 검증에 걸린다). 유형을 추가할 때는 모델 + 마이그레이션도 함께.

export const EVENT_TYPE_WORK = "업무일정";
export const EVENT_TYPE_EVENT = "행사";
export const EVENT_TYPE_WORK_TIME_CHANGE = "근무시간 변동";
export const EVENT_TYPE_PREGNANCY_REDUCTION = "임신기 근로시간 단축";
export const EVENT_TYPE_CONDOLENCE_LEAVE = "경조사휴가";

// 좌측 "일정 유형 (전체)" 필터에 뜨는 순서 그대로.
//
// ⚠️ "공지사항"은 event_type이 아니다 — CalendarEvent.is_notice(boolean)다.
// 필터 목록에서만 유형처럼 보이므로 색상표에는 두되, 아래 SELECTABLE_EVENT_TYPES
// (생성/수정 폼의 선택지)에서는 빠진다.
export const EVENT_TYPE_COLORS = {
  "공지사항": "#ffffff",
  "행사": "#90caf9",
  "업무일정": "#4a90e2",
  // 근무시간 변동 — "근무 아님"의 시각 신호로 무채색. 나머지가 전부 파스텔이라
  // 진한 회색이면 한눈에 구분된다. 업무성과 집계에서도 빠진다(백엔드 단일 판정).
  "근무시간 변동": "#616161",
  // 임신기 근로시간 단축 — 근무시간 변동과 같은 "근무 아님" 계열이지만 축하·배려의
  // 의미가 있어 회색을 쓰지 않는다. 연분홍은 대체휴무(#f8bbd0)와, 연산호는
  // 특별휴가(#ffe0b2)·연차(#fff9c4)와 겹쳐 보라 계열이 유일하게 빈 색상 구간이다.
  // 파스텔 100톤보다 한 단계 진한 200톤이라 명도까지 달라 나란히 놓아도 구분된다.
  "임신기 근로시간 단축": "#ce93d8",
  "연차": "#fff9c4",
  "반차": "#c8e6c9",
  "대체휴무": "#f8bbd0",
  "특별휴가": "#ffe0b2",
  // 경조사휴가 — 휴가 계열 파스텔(100톤)에서 비어 있던 남보라(Indigo 100).
  // 기존 9색과의 CIEDE2000 최소 거리가 후보 중 가장 컸다(행사 15.3 / 공지 16.8 /
  // 임신기 18.6) — 기존 최근접 쌍(연차↔특별휴가 9.9)보다 멀다. 경사·조사 양쪽에
  // 어울리게 채도가 낮은 차분한 색을 골랐다. 칩 위 검정 글씨 대비 13:1.
  "경조사휴가": "#c5cae9",
};

// 일정 생성/수정 폼의 유형 선택지 (= 실제 event_type만, 공지사항 제외)
export const SELECTABLE_EVENT_TYPES = [
  "행사",
  "업무일정",
  "근무시간 변동",
  "임신기 근로시간 단축",
  "연차",
  "반차",
  "대체휴무",
  "특별휴가",
  "경조사휴가",
];

// ✅ 연차·반차는 전자결재 휴가신청서로만 신청한다(2026-10-19 일원화). 캘린더 관리자(position_rank 2·3)만
// 소급·정정용으로 직접 등록·수정·삭제한다. 서버(calendar_app serializer·views)도 같은 규칙으로 막는다.
export const REQUEST_ONLY_LEAVE_TYPES = ["연차", "반차"];
export const REQUEST_ONLY_NOTICE = "연차·반차는 전자결재 > 휴가신청서로 신청하세요.";

// 로그인 때 저장한 내 정보(/auth/me/ — id, position_rank)
export const currentUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};
export const isCalendarAdmin = (user) => [2, 3].includes(user?.position_rank);

// 이 사용자가 고를 수 있는 일정 유형 — 관리자가 아니면 연차·반차를 뺀다
export const selectableEventTypesFor = (user) =>
  isCalendarAdmin(user)
    ? SELECTABLE_EVENT_TYPES
    : SELECTABLE_EVENT_TYPES.filter((t) => !REQUEST_ONLY_LEAVE_TYPES.includes(t));

// 제목을 사용자가 직접 입력하는 유형.
// (업무일정은 "장소 + 업무종류"로, 차감 휴가 4종은 "연차 N시간 사용"으로 서버가 자동 생성한다)
export const TITLE_INPUT_TYPES = [
  "행사", "근무시간 변동", "임신기 근로시간 단축", "경조사휴가",
];

// 기간을 하루 단위로 쪼개 등록하는 유형 — 주말·공휴일은 자동 제외된다.
// 휴가 4종은 잔여시간 차감 경로가 따로 있어 이 목록에 넣지 않는다.
// 경조사휴가는 휴가지만 차감이 없어 여기 들어간다(하루 단위 분할 + 주말·공휴일 제외).
export const WORKDAY_SPLIT_TYPES = [
  "업무일정", "행사", "근무시간 변동", "임신기 근로시간 단축", "경조사휴가",
];

// 공휴일 조회가 필요한 유형 (등록 대상 근무일에서 공휴일을 빼야 하는 유형 전부)
export const NEEDS_HOLIDAY_LOOKUP_TYPES = [
  ...WORKDAY_SPLIT_TYPES,
  "연차",
  "반차",
  "대체휴무",
  "특별휴가",
];

// 업무종류(work_category)·난이도·이동시간 등 성과지표를 입력받는 유형.
// ⚠️ "업무일정"뿐이다. 행사·근무시간 변동·임신기 근로시간 단축·경조사휴가는 제목만 받는다.
export const PERFORMANCE_INPUT_TYPES = ["업무일정"];
